import { UnknownAction } from 'redux';

import globalLogger from '../logger';
import { MiddlewareNextFunction, ReduxState, ReduxStore } from '../types/reduxTypes';
import { DRAG_END, DRAG_START } from '../reducers/draggingConstants';
import { LINE_POINT_DRAG_START } from '../element/line/lineUiConstants';
import {
    ELEMENT_CREATE,
    ELEMENT_CREATION_SOURCES,
    ELEMENT_MOVE_MULTI,
    ELEMENT_MOVE_OPERATIONS,
    ELEMENT_SET_TYPE,
    ELEMENT_UPDATE,
    ELEMENT_UPDATE_TYPE,
} from '../../common/elements/elementConstants';
import {
    ElementCreateAction,
    isActionElementMoveMulti,
    isActionElementUpdate,
} from '../../common/elements/elementActionTypes';
import {
    ELEMENTS_DESELECT_ALL,
    ELEMENTS_DESELECTED,
    ELEMENTS_SELECTED,
} from '../../common/elements/selectionConstants';
import { CommentActionType } from '../../common/comments/commentConstants';
import { POPUP_OPEN, POPUP_TOGGLE } from '../components/popupPanel/popupConstants';
import { PopupToggleAction } from '../components/popupPanel/popupActionTypes';
import { BATCH_ACTION_TYPE } from '../../common/store/reduxBulkingMiddleware';
import { BoardSections } from '../../common/boards/boardConstants';
import { ElementType } from '../../common/elements/elementTypes';
import { getElements } from '../element/selectors/elementsSelector';
import { activePopupSelector } from '../components/popupPanel/popupOpenSelector';
import { getElement } from '../../common/elements/utils/elementTraversalUtils';
import { isColumn, isLink, isTask } from '../../common/elements/utils/elementTypeUtils';
import { getIsSoundsEnabledForCurrentUser } from './soundEnabledSelector';
import { playSound } from './soundService';
import { SoundId } from './soundConstants';

const logger = globalLogger.createChannel('sounds');

/** Fields the socket / bulking layers can add to any action */
type ActionMeta = { remote?: boolean; batched?: boolean };

// Local shapes for actions whose creators don't export a type
type DragStartAction = UnknownAction & { ids?: string[]; elementType?: string };
type DragEndAction = UnknownAction & { dropState?: { droppedElementIds?: string[] } };
type LinePointDragStartAction = UnknownAction & { isControlPoint?: boolean };
type ElementSetTypeAction = UnknownAction & { id?: string };
type SelectionAction = UnknownAction & { ids?: string[] };
type CommentUpdateAction = UnknownAction & { contentUpdate?: { updateType?: string } };
type BatchAction = UnknownAction & { payload?: UnknownAction[] };

type SoundRuleContext = {
    action: UnknownAction & ActionMeta;
    /** Store state captured before the action was reduced */
    prevState: ReduxState;
};

type SoundRule = (context: SoundRuleContext) => SoundId | SoundId[] | undefined;

const isRemote = (action: UnknownAction & ActionMeta) => !!action.remote;

const getElementsById = (state: ReduxState, ids: string[] | undefined) => {
    if (!ids?.length) return [];
    const elements = getElements(state);
    return ids.map((id) => getElement(elements, id)).filter(Boolean);
};

// ---------------------------------------------------------------------------
// Element pickup / drop
// ---------------------------------------------------------------------------

// Every element type shares the card pickup / drop sounds. Existing elements arrive as ids;
// toolbar creation-tool drags carry an `elementType` but no ids yet — either way, something is
// being dragged, so it sounds.
const dragStartRule: SoundRule = ({ action }) => {
    const { ids, elementType } = action as DragStartAction;
    return ids?.length || elementType ? SoundId.CardPickup : undefined;
};

// Grabbing a line endpoint counts as a pickup too; dragging the curve's control point stays silent.
// (The matching drop is component-driven — see ConnectedEdgeDragHandle.jsx — because only the drop
// target knows whether the edge connected to an element.)
const linePointDragStartRule: SoundRule = ({ action }) =>
    (action as LinePointDragStartAction).isControlPoint ? undefined : SoundId.CardPickup;

// Cancelled drags dispatch dragEnd() with no dropState — only successful drops make a sound
const dragEndRule: SoundRule = ({ action }) =>
    (action as DragEndAction).dropState?.droppedElementIds?.length ? SoundId.CardDrop : undefined;

// ---------------------------------------------------------------------------
// Element updates: column collapse, legacy task completion, reactions
// ---------------------------------------------------------------------------

const elementUpdateRule: SoundRule = ({ action, prevState }) => {
    if (!isActionElementUpdate(action) || isRemote(action)) return undefined;

    if (action.updateType === ELEMENT_UPDATE_TYPE.REACTION) return SoundId.ReactionAdd;

    // Legacy (element-per-task) task lists
    if (action.updateType === ELEMENT_UPDATE_TYPE.TOGGLE_COMPLETION) {
        const update = action.updates[0];
        if (!update) return undefined;
        return update.changes?.isComplete ? SoundId.CheckboxTick : SoundId.CheckboxUntick;
    }

    // Column collapse / expand
    const elements = getElements(prevState);
    const collapseUpdate = action.updates.find(
        (update) => update.changes?.collapsed !== undefined && isColumn(getElement(elements, update.id)),
    );
    if (collapseUpdate) return collapseUpdate.changes.collapsed ? SoundId.Contract : SoundId.Expand;

    return undefined;
};

/**
 * Remember what was just created, so e.g. the first task that comes with a brand-new task list
 * doesn't sound like a task being added.
 */
const RECENTLY_CREATED_WINDOW_MS = 1000;
const recentlyCreatedElementIds = new Map<string, number>();

const rememberCreatedElement = (id: string) => {
    const now = Date.now();
    recentlyCreatedElementIds.set(id, now);
    recentlyCreatedElementIds.forEach((createdAt, createdId) => {
        if (now - createdAt > RECENTLY_CREATED_WINDOW_MS) recentlyCreatedElementIds.delete(createdId);
    });
};

const wasRecentlyCreated = (id: string | undefined) => {
    if (!id) return false;
    const createdAt = recentlyCreatedElementIds.get(id);
    return createdAt !== undefined && Date.now() - createdAt <= RECENTLY_CREATED_WINDOW_MS;
};

const IN_PLACE_CREATION_SOURCES = new Set<string>([
    ELEMENT_CREATION_SOURCES.DOUBLE_CLICK,
    ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
]);

const elementCreateRule: SoundRule = ({ action }) => {
    if (isRemote(action)) return undefined;

    const createAction = action as ElementCreateAction;
    if (createAction.id) rememberCreatedElement(createAction.id);

    // Legacy task lists: the first task that comes with a freshly created list is part of creating
    // the list (which has already sounded), not a task being added — stay quiet
    if (createAction.elementType === ElementType.TASK_TYPE && wasRecentlyCreated(createAction.location?.parentId)) {
        return undefined;
    }

    // Creating an element in place — double-clicking the canvas or picking one from the context menu —
    // sounds like a drop
    if (createAction.creationSource && IN_PLACE_CREATION_SOURCES.has(createAction.creationSource)) {
        return SoundId.CardDrop;
    }

    // Legacy task lists: a new task element appearing
    return createAction.elementType === ElementType.TASK_TYPE ? SoundId.TaskNewTaskAppear : undefined;
};

// A link card resolving its URL (fetching title / preview, possibly becoming an image or board
// alias) lands as ELEMENT_SET_TYPE on an element that was a link — it "expands" into its content
const elementSetTypeRule: SoundRule = ({ action, prevState }) => {
    if (isRemote(action)) return undefined;
    const previousElement = getElement(getElements(prevState), (action as ElementSetTypeAction).id);
    return isLink(previousElement) ? SoundId.Expand : undefined;
};

// ---------------------------------------------------------------------------
// Delete (move to trash)
// ---------------------------------------------------------------------------

const elementMoveMultiRule: SoundRule = ({ action, prevState }) => {
    if (!isActionElementMoveMulti(action) || isRemote(action)) return undefined;

    const isTrash =
        action.moveOperation === ELEMENT_MOVE_OPERATIONS.TRASH ||
        action.moves.some((move) => move.location?.section === BoardSections.TRASH);
    if (!isTrash) return undefined;

    // Deleting individual task items (legacy task lists) isn't "deleting an element" — stay quiet
    // unless at least one whole (non-task) element is going to the trash
    const movedIds = action.moves.map((move) => move.id);
    const movedElements = getElementsById(prevState, movedIds);
    if (movedElements.length && movedElements.every(isTask)) return undefined;

    return SoundId.DeleteSomething;
};

// ---------------------------------------------------------------------------
// Selection (remote users only — local select / deselect are silent)
// ---------------------------------------------------------------------------

const elementsSelectedRule: SoundRule = ({ action }) =>
    isRemote(action) && (action as SelectionAction).ids?.length ? SoundId.RemoteUserSelect : undefined;

const elementsDeselectedRule: SoundRule = ({ action }) =>
    isRemote(action) && (action as SelectionAction).ids?.length ? SoundId.RemoteUserDeselect : undefined;

// deselectAll carries no ids (see selectionActions.js) — any remote deselect-all counts
const elementsDeselectAllRule: SoundRule = ({ action }) =>
    isRemote(action) ? SoundId.RemoteUserDeselect : undefined;

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

// A comment being added — by the local user or a collaborator alike
const commentsAddRule: SoundRule = ({ action }) => {
    // Bulk-loaded comments (e.g. expanding a collapsed thread) are marked batched
    if (action.batched && !isRemote(action)) return undefined;
    return SoundId.CommentAdd;
};

// Starting a new (empty) comment in a thread — reuses the "new task appears" sound
const commentsStartEditingRule: SoundRule = () => SoundId.TaskNewTaskAppear;

const commentsUpdateRule: SoundRule = ({ action }) => {
    if (isRemote(action)) return undefined;
    const { contentUpdate } = action as CommentUpdateAction;
    return contentUpdate?.updateType === ELEMENT_UPDATE_TYPE.REACTION ? SoundId.ReactionAdd : undefined;
};

// ---------------------------------------------------------------------------
// Popups
// ---------------------------------------------------------------------------

const popupOpenRule: SoundRule = () => SoundId.PopupOpen;

// A toggle only sounds when it opens the popup (closing is silent)
const popupToggleRule: SoundRule = ({ action, prevState }) =>
    activePopupSelector(prevState)?.has((action as Partial<PopupToggleAction>).popupId) ? undefined : SoundId.PopupOpen;

// ---------------------------------------------------------------------------
// Rule table
// ---------------------------------------------------------------------------

const SOUND_RULES: Record<string, SoundRule> = {
    [DRAG_START]: dragStartRule,
    [DRAG_END]: dragEndRule,
    [LINE_POINT_DRAG_START]: linePointDragStartRule,
    [ELEMENT_UPDATE]: elementUpdateRule,
    [ELEMENT_CREATE]: elementCreateRule,
    [ELEMENT_SET_TYPE]: elementSetTypeRule,
    [ELEMENT_MOVE_MULTI]: elementMoveMultiRule,
    [ELEMENTS_SELECTED]: elementsSelectedRule,
    [ELEMENTS_DESELECTED]: elementsDeselectedRule,
    [ELEMENTS_DESELECT_ALL]: elementsDeselectAllRule,
    [CommentActionType.COMMENTS_ADD]: commentsAddRule,
    [CommentActionType.COMMENTS_UPDATE]: commentsUpdateRule,
    [CommentActionType.COMMENTS_START_EDITING]: commentsStartEditingRule,
    [POPUP_OPEN]: popupOpenRule,
    [POPUP_TOGGLE]: popupToggleRule,
};

/** Collects the sounds an action (or each action inside a batch) should trigger. */
const getSoundsForAction = (context: SoundRuleContext): SoundId[] => {
    const { action } = context;

    if (action.type === BATCH_ACTION_TYPE) {
        const payload = (action as BatchAction).payload || [];
        return payload.flatMap((batchedAction) => getSoundsForAction({ ...context, action: batchedAction }));
    }

    const rule = SOUND_RULES[action.type];
    if (!rule) return [];

    try {
        const result = rule(context);
        if (!result) return [];
        return Array.isArray(result) ? result : [result];
    } catch (error) {
        logger.warn(`Sound rule failed for action: ${action.type}`, error);
        return [];
    }
};

/**
 * Plays UX sounds in response to redux actions. Central hook point for action-driven sounds
 * so individual components don't need to know about audio. Component-driven sounds
 * (hover, toggles, drawing…) use usePlaySound instead.
 */
export default (store: ReduxStore) => (next: MiddlewareNextFunction) => (action: UnknownAction) => {
    const isRelevant = action.type === BATCH_ACTION_TYPE || !!SOUND_RULES[action.type];
    if (!isRelevant) return next(action);

    const prevState = store.getState();
    const result = next(action);

    if (!getIsSoundsEnabledForCurrentUser(prevState)) return result;

    const sounds = getSoundsForAction({ action, prevState });
    // De-dupe so e.g. a batch of 20 remote selections plays once
    new Set(sounds).forEach((soundId) => playSound(soundId));

    return result;
};
